import React, { useState, useEffect, useMemo } from 'react';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Loader2, Factory, CheckCircle2, AlertCircle, Pause } from 'lucide-react';

const TYPES = [
  { value: 'milk', label: 'Milk supplier' },
  { value: 'ingredient', label: 'Ingredient supplier' },
  { value: 'packaging', label: 'Packaging supplier' },
  { value: 'service', label: 'Service supplier' },
  { value: 'other', label: 'Other' },
];
const STATUSES = [
  { value: 'approved', label: 'Approved', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'pending', label: 'Pending', icon: AlertCircle, color: 'bg-amber-100 text-amber-700' },
  { value: 'suspended', label: 'Suspended', icon: Pause, color: 'bg-zinc-200 text-zinc-700' },
];

const empty = {
  name: '', code: '', supplier_type: 'ingredient', approval_status: 'approved',
  contact_name: '', email: '', phone: '',
  address_line1: '', address_line2: '', city: '', country: 'Ireland',
  notes: '',
};

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const r = await getSuppliers();
      setItems(r.data);
    } catch (e) { toast.error('Failed to load suppliers'); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => items.filter(s => {
    if (typeFilter !== 'all' && s.supplier_type !== typeFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.name || '').toLowerCase().includes(q)
      || (s.code || '').toLowerCase().includes(q)
      || (s.contact_name || '').toLowerCase().includes(q);
  }), [items, search, typeFilter]);

  const openNew = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name?.trim()) { toast.error('Supplier name is required'); return; }
    setSaving(true);
    try {
      if (editing) await updateSupplier(editing.id, form);
      else await createSupplier(form);
      toast.success(editing ? 'Supplier updated' : 'Supplier created');
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}? This soft-deletes it.`)) return;
    try { await deleteSupplier(s.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="suppliers-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><Factory className="w-5 h-5" />Suppliers</h1>
          <p className="text-sm text-muted-foreground">Inwards · the businesses you receive milk, ingredients and packaging from.</p>
        </div>
        <Button onClick={openNew} data-testid="btn-new-supplier"><Plus className="w-4 h-4 mr-1" />New supplier</Button>
      </div>

      <Card><CardContent className="pt-4 flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, code, contact…" className="pl-9" data-testid="supplier-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[200px]" data-testid="supplier-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardContent></Card>

      {loading ? <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> :
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Supplier</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Location</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">No suppliers yet.</td></tr> :
                filtered.map(s => {
                  const st = STATUSES.find(x => x.value === s.approval_status) || STATUSES[0];
                  const SIcon = st.icon;
                  const t = TYPES.find(x => x.value === s.supplier_type);
                  return (
                    <tr key={s.id} className="border-t hover:bg-zinc-50" data-testid={`supplier-row-${s.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{s.name}</div>
                        <div className="text-xs text-muted-foreground">{s.code}</div>
                      </td>
                      <td className="px-4 py-3">{t?.label || s.supplier_type}</td>
                      <td className="px-4 py-3">
                        <div>{s.contact_name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{s.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">{[s.city, s.country].filter(Boolean).join(', ')}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`${st.color} border-0`}><SIcon className="w-3 h-3 mr-1" />{st.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`btn-edit-${s.id}`}><Edit className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(s)} data-testid={`btn-delete-${s.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </CardContent></Card>}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="supplier-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit supplier' : 'New supplier'}</DialogTitle>
            <DialogDescription>Suppliers are anyone you receive goods or services from. Keep it simple — name + type + approval is enough to get going.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2"><Label>Supplier name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" /></div>
            <div><Label>Supplier code</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="e.g. SUP-MILK-01" /></div>
            <div><Label>Type</Label>
              <Select value={form.supplier_type} onValueChange={v => setForm({ ...form, supplier_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Approval status</Label>
              <Select value={form.approval_status} onValueChange={v => setForm({ ...form, approval_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Contact person</Label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={form.address_line1} onChange={e => setForm({ ...form, address_line1: e.target.value })} /></div>
            <div><Label>Country</Label><Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-supplier">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
